import { DateTime } from 'luxon';

import type {
    SettingValueMap,
    FilterData,
    View} from 'portlet/models';
import {
    FilterValue,
    Specification,
    ListenTo,
} from 'portlet/models';

import { InteractionToFilterService } from './InteractionToFilterService';

export class AutoDateService {
    /** wrapping `new Date()` for easier mocking/testing */
    public now(): Date {
        return new Date();
    }

    public static evaluateAutoDate(
        spec: Specification,
        view: View,
        settings: Partial<SettingValueMap>,
        now: Date,
    ): FilterData | null {
        const { DATE_TYPE: dateTypeSetting, AUTO_DATE: autoDateSetting } = settings;
        const observedFilters = Specification.getObservedFilters(spec);
        const listenTo = ListenTo.materializeListenTo(observedFilters, settings);
        const filterItemToEmit = InteractionToFilterService.matchFirstListenToByKeywords(listenTo, [
            'autodate',
            'date',
        ]);

        if (!dateTypeSetting || !autoDateSetting || !filterItemToEmit) return null;

        const value = AutoDateService.calculateAutoDateFilterValue(
            dateTypeSetting,
            autoDateSetting,
            now,
        );

        return value ? { item: filterItemToEmit.item, value } : null;
    }

    private static calculateAutoDateFilterValue(
        dateType: SettingValueMap['DATE_TYPE'],
        autoDateConfig: SettingValueMap['AUTO_DATE'],
        now: Date,
    ): FilterValue | null {
        if (!autoDateConfig) return null;

        const dateTime = DateTime.fromJSDate(now, { zone: 'utc' });
        const autoDateFromTime = AutoDateService.applyOffset(
            dateType,
            dateTime,
            autoDateConfig.from.offset,
        );

        if (!autoDateConfig.to)
            return FilterValue.DATE({ dateType, value: autoDateFromTime.toISO() });

        const autoDateToTime = AutoDateService.applyOffset(
            dateType,
            dateTime,
            autoDateConfig.to.offset,
        );

        return FilterValue.DATE_RANGE({
            dateType,
            value: [autoDateFromTime.toISO(), autoDateToTime.toISO()],
        });
    }

    private static applyOffset(
        dateType: SettingValueMap['DATE_TYPE'],
        date: DateTime,
        offset: number,
    ): DateTime {
        switch (dateType) {
            case 'date':
                return date.plus({ day: offset });
            case 'month':
                return date.plus({ month: offset });
            case 'week':
                return date.plus({ week: offset });
            case 'year':
                return date.plus({ year: offset });
        }
    }
}
